<template>
  <div class="aml-detail-root">

    <!-- ══ Loading ══ -->
    <div v-if="loading" class="aml-detail-wrap">
      <div class="aml-detail-header">
        <v-btn icon disabled class="mr-2"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <v-skeleton-loader type="heading" width="260" class="mb-1" />
      </div>
      <v-row>
        <v-col cols="12" md="7"><v-skeleton-loader type="card" height="280" class="mb-4" /></v-col>
        <v-col cols="12" md="5"><v-skeleton-loader type="card" height="220" /></v-col>
      </v-row>
    </div>

    <!-- ══ Error ══ -->
    <div v-else-if="!flag" class="aml-detail-wrap text-center py-12">
      <v-icon size="52" color="grey lighten-1">mdi-file-alert-outline</v-icon>
      <div class="subtitle-1 grey--text mt-3">Flag could not be loaded</div>
      <v-btn depressed small color="primary" class="mt-4" @click="$router.push({ name: 'watchlist-dashboard' })">
        <v-icon small left>mdi-arrow-left</v-icon>Back to dashboard
      </v-btn>
    </div>

    <!-- ══ Content ══ -->
    <div v-else class="aml-detail-wrap">

      <!-- ── Header ── -->
      <div class="aml-detail-header">
        <button class="aml-back-btn" @click="$router.push({ name: 'watchlist-dashboard' })">
          <v-icon small>mdi-arrow-left</v-icon>
        </button>
        <div class="flex-grow-1 ml-3">
          <div class="aml-case-ref">{{ providerShort(flag.provider) }} — {{ flag.listName }}</div>
          <div class="aml-case-sub">Account {{ flag.mainAccountNumber }} · Flagged {{ formatDate(flag.dateCreated, 'YYYY-MM-DD') }}</div>
        </div>
        <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
          <span class="aml-status-pill" :style="flagStatusStyle(flag.flagStatus)">{{ flagStatusLabel(flag.flagStatus) }}</span>
          <template v-if="canOpenCase">
            <div class="aml-header-divider" />
            <!-- Label changes depending on whether an active case already exists -->
            <button class="aml-hdr-btn aml-hdr-btn--primary" @click="openCaseDialogAndLoadUsers">
              <v-icon x-small left>{{ hasActiveCase ? 'mdi-folder-plus-outline' : 'mdi-folder-open-outline' }}</v-icon>
              {{ hasActiveCase ? 'Add to existing case' : 'Open investigation' }}
            </button>
            <!-- Quick link to active case if one exists -->
            <button v-if="hasActiveCase && activeCaseRef" class="aml-hdr-btn" @click="goToCase(activeCaseRef.id)">
              <v-icon x-small left>mdi-eye</v-icon>View case {{ activeCaseRef.caseReference }}
            </button>
          </template>
          <template v-if="canSetOverride">
            <button class="aml-hdr-btn" @click="overrideDialog = true">
              <v-icon x-small left>mdi-refresh</v-icon>Allow re-search
            </button>
          </template>
        </div>
      </div>

      <!-- ── Tabs ── -->
      <div class="aml-tabs-bar">
        <button v-for="(t, i) in tabDefs" :key="i" class="aml-tab" :class="{ 'aml-tab--active': tab === i }" @click="tab = i">
          {{ t.label }}
          <span v-if="t.badge !== undefined" class="aml-tab-badge">{{ t.badge }}</span>
        </button>
      </div>

      <!-- ══ TAB 0 — Flag overview ══ -->
      <div v-show="tab === 0" class="aml-tab-content">
        <v-row dense>

          <!-- Flag info -->
          <v-col cols="12" md="7">
            <div class="aml-panel mb-4">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#FCEBEB">
                  <v-icon small color="#A32D2D">mdi-alert-circle-outline</v-icon>
                </div>
                <span class="aml-panel-title">Flag details</span>
                <v-spacer />
                <span class="aml-tag" :style="flagStatusStyle(flag.flagStatus)">{{ flagStatusLabel(flag.flagStatus) }}</span>
              </div>
              <div class="pa-3">
                <div class="aml-grid-2 mb-3">
                  <div class="aml-field">
                    <div class="aml-label">Provider</div>
                    <div class="aml-value font-weight-medium">{{ providerLabel(flag.provider) }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">List name</div>
                    <div class="aml-value">{{ flag.listName || '—' }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Account</div>
                    <div class="aml-value">{{ flag.mainAccountNumber }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Account frozen</div>
                    <div class="aml-value">
                      <v-chip x-small :color="flag.accountFrozen ? 'red' : 'green'" dark>{{ flag.accountFrozen ? 'Yes' : 'No' }}</v-chip>
                    </div>
                  </div>
                  <div v-if="flag.dateReviewed" class="aml-field">
                    <div class="aml-label">Date reviewed</div>
                    <div class="aml-value">{{ formatDate(flag.dateReviewed, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="flag.reviewedBy" class="aml-field">
                    <div class="aml-label">Reviewed by</div>
                    <div class="aml-value">{{ assigneeName(flag.reviewedBy) }}</div>
                  </div>
                </div>

                <template v-if="flag.reviewOutcome || flag.reviewNote">
                  <div class="aml-divider mb-3" />
                  <div v-if="flag.reviewOutcome" class="aml-field mb-2">
                    <div class="aml-label">Review outcome</div>
                    <div class="aml-value">{{ flag.reviewOutcome }}</div>
                  </div>
                  <div v-if="flag.reviewNote" class="aml-field">
                    <div class="aml-label">Review note</div>
                    <div class="aml-value" style="white-space:pre-wrap">{{ flag.reviewNote }}</div>
                  </div>
                </template>

                <!-- Prior outcome -->
                <template v-if="flag.lastReviewedStatus">
                  <div class="aml-divider my-3" />
                  <div class="aml-section-label mb-2">Previous outcome</div>
                  <div class="aml-grid-2">
                    <div class="aml-field">
                      <div class="aml-label">Prior status</div>
                      <div class="aml-value"><span class="aml-pill" :style="flagStatusStyle(flag.lastReviewedStatus)">{{ flagStatusLabel(flag.lastReviewedStatus) }}</span></div>
                    </div>
                    <div v-if="flag.lastReviewedAt" class="aml-field">
                      <div class="aml-label">Prior review date</div>
                      <div class="aml-value">{{ formatDate(flag.lastReviewedAt, 'YYYY-MM-DD') }}</div>
                    </div>
                    <div v-if="flag.lastReviewedBy" class="aml-field">
                      <div class="aml-label">Prior reviewer</div>
                      <div class="aml-value">{{ assigneeName(flag.lastReviewedBy) }}</div>
                    </div>
                    <div v-if="flag.lastReviewOutcome" class="aml-field">
                      <div class="aml-label">Prior outcome</div>
                      <div class="aml-value">{{ flag.lastReviewOutcome }}</div>
                    </div>
                  </div>
                </template>

                <!-- Re-search override info -->
                <template v-if="flag.allowResearch">
                  <div class="aml-divider my-3" />
                  <div class="aml-section-label mb-2" style="color:#854F0B">Re-search override active</div>
                  <div class="aml-grid-2">
                    <div class="aml-field">
                      <div class="aml-label">Set by</div>
                      <div class="aml-value">{{ assigneeName(flag.researchOverrideByUser) }}</div>
                    </div>
                    <div v-if="flag.researchOverrideAt" class="aml-field">
                      <div class="aml-label">Set on</div>
                      <div class="aml-value">{{ formatDate(flag.researchOverrideAt, 'YYYY-MM-DD') }}</div>
                    </div>
                    <div v-if="flag.researchOverrideReason" class="aml-field" style="grid-column:1/-1">
                      <div class="aml-label">Reason</div>
                      <div class="aml-value">{{ flag.researchOverrideReason }}</div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Linked cases -->
            <div v-if="flag.cases && flag.cases.length" class="aml-panel">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#EEEDFE">
                  <v-icon small color="#534AB7">mdi-folder-outline</v-icon>
                </div>
                <span class="aml-panel-title">Linked cases</span>
                <span class="aml-tab-badge ml-2">{{ flag.cases.length }}</span>
              </div>
              <table class="aml-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Status</th>
                    <th>Stage</th>
                    <th>Opened</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="c in flag.cases" :key="c.id" class="aml-table-row" @click="goToCase(c.id)">
                    <td><span class="aml-ref-link">{{ c.caseReference }}</span></td>
                    <td><span class="aml-pill" :style="caseStatusStyle(c.status)">{{ formatStatus(c.status) }}</span></td>
                    <td class="aml-muted">{{ c.reportStage || '—' }}</td>
                    <td class="aml-muted">{{ fmtDate(c.dateCreated) }}</td>
                    <td><v-btn icon x-small @click.stop="goToCase(c.id)"><v-icon x-small color="#185FA5">mdi-eye</v-icon></v-btn></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </v-col>

          <!-- Most recent search result -->
          <v-col cols="12" md="5">
            <div class="aml-panel mb-4">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#E6F1FB">
                  <v-icon small color="#185FA5">mdi-database-search-outline</v-icon>
                </div>
                <span class="aml-panel-title">Latest search result</span>
                <span v-if="latestLog" class="aml-muted ml-auto" style="font-size:11px">{{ formatDate(latestLog.dateCreated, 'YYYY-MM-DD') }}</span>
              </div>
              <div v-if="!latestLog" class="aml-empty-panel">No search logs found.</div>
              <div v-else class="pa-3">
                <div class="aml-grid-2 mb-3">
                  <div class="aml-field">
                    <div class="aml-label">Match status</div>
                    <div class="aml-value">
                      <span class="aml-pill" :style="matchStatusStyle(latestLog.matchStatus)">{{ latestLog.matchStatus }}</span>
                    </div>
                  </div>
                  <div v-if="latestLog.matchScore" class="aml-field">
                    <div class="aml-label">Score</div>
                    <div class="aml-value">{{ latestLog.matchScore }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Trigger</div>
                    <div class="aml-value">{{ latestLog.triggerType }}</div>
                  </div>
                  <div v-if="latestLog.triggeredBy" class="aml-field">
                    <div class="aml-label">Triggered by</div>
                    <div class="aml-value">{{ assigneeName(latestLog.triggeredBy) }}</div>
                  </div>
                </div>

                <!-- Match details -->
                <template v-if="latestLog.matchDetails && latestLog.matchDetails.length">
                  <div class="aml-divider mb-3" />
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="aml-section-label">Matched records ({{ latestLog.matchDetails.length }})</span>
                    <button class="aml-show-more-btn" @click="latestHitsExpanded = !latestHitsExpanded">
                      <v-icon x-small>{{ latestHitsExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
                      {{ latestHitsExpanded ? 'Collapse' : 'Show all' }}
                    </button>
                  </div>
                  <div
                    v-for="(hit, i) in latestHitsExpanded ? latestLog.matchDetails : latestLog.matchDetails.slice(0, latestHitsPerPage)"
                    :key="i"
                    class="aml-hit-card mb-2"
                    style="cursor:pointer"
                    @click="$set(expandedLatestHits, i, !expandedLatestHits[i])"
                  >
                    <!-- Compact row -->
                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center" style="gap:6px;flex:1;min-width:0">
                        <v-icon x-small :color="expandedLatestHits[i] ? '#185FA5' : '#bbb'">
                          {{ expandedLatestHits[i] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span style="font-size:12px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ hit.name }}</span>
                        <span class="aml-type-badge">{{ hit.source_code }}</span>
                        <span class="aml-type-badge">{{ hit.data_set }}</span>
                      </div>
                      <span class="aml-pill" style="background:#FCEBEB;color:#A32D2D;flex-shrink:0">{{ hit.score }}</span>
                    </div>
                    <!-- Expanded detail -->
                    <div v-if="expandedLatestHits[i]" class="mt-2 pt-2" style="border-top:0.5px solid #eee">
                      <div v-if="hit.aliases" class="aml-muted mb-1" style="font-size:11px"><strong>Aliases:</strong> {{ hit.aliases }}</div>
                      <div v-if="hit.address" class="aml-muted mb-1" style="font-size:11px"><strong>Address:</strong> {{ hit.address }}</div>
                      <div v-if="hit.last_updated" class="aml-muted mb-1" style="font-size:11px"><strong>List date:</strong> {{ describeListDate(hit.last_updated, hit.date_method) }}</div>
                      <a v-if="isSafeUrl(hit.source_url)" :href="hit.source_url" target="_blank" rel="noopener noreferrer" class="aml-source-link mb-1" @click.stop>
                        <v-icon x-small color="#185FA5">mdi-open-in-new</v-icon>View at source
                      </a>
                      <div class="d-flex" style="gap:4px;flex-wrap:wrap">
                        <span class="aml-type-badge">{{ hit.source_name }}</span>
                        <span class="aml-type-badge">{{ hit.type }}</span>
                      </div>
                    </div>
                  </div>
                  <div v-if="!latestHitsExpanded && latestLog.matchDetails.length > latestHitsPerPage" class="mt-1" style="font-size:11px;color:#aaa;text-align:center">
                    + {{ latestLog.matchDetails.length - latestHitsPerPage }} more — click "Show all" to expand
                  </div>
                </template>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- ══ TAB 1 Search history ══ -->
      <div v-show="tab === 1" class="aml-tab-content">
        <div class="aml-panel">
          <div class="aml-panel-head">
            <div class="aml-panel-icon" style="background:#E6F1FB">
              <v-icon small color="#185FA5">mdi-history</v-icon>
            </div>
            <span class="aml-panel-title">Search history</span>
            <span class="aml-tab-badge ml-2">{{ flag.searchLogs ? flag.searchLogs.length : 0 }}</span>
          </div>
          <div v-if="!flag.searchLogs || !flag.searchLogs.length" class="aml-empty-panel">No searches recorded.</div>
          <table v-else class="aml-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Trigger</th>
                <th>Match status</th>
                <th>Score</th>
                <th>By</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in flag.searchLogs"
                :key="log.id"
                class="aml-table-row"
                @click="expandedLog = expandedLog === log.id ? null : log.id"
              >
                <td class="aml-muted">{{ formatDate(log.dateCreated, 'YYYY-MM-DD HH:mm') }}</td>
                <td><span class="aml-type-badge">{{ log.triggerType }}</span></td>
                <td><span class="aml-pill" :style="matchStatusStyle(log.matchStatus)">{{ log.matchStatus }}</span></td>
                <td class="aml-muted">{{ log.matchScore || '—' }}</td>
                <td class="aml-muted">{{ log.triggeredBy ? assigneeName(log.triggeredBy) : 'System' }}</td>
                <td class="aml-muted" style="font-size:11px;max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ log.triggerReason || '—' }}</td>
              </tr>
              <!-- Expanded log detail -->
              <template v-for="log in flag.searchLogs">
                <tr v-if="expandedLog === log.id" :key="'exp-' + log.id">
                  <td colspan="6" class="pa-3" style="background:#fafafa">
                    <div class="aml-section-label mb-2">Match details</div>
                    <div v-if="!log.matchDetails || !log.matchDetails.length" class="aml-muted" style="font-size:12px">No matches returned.</div>
                    <div
                      v-for="(hit, i) in log.matchDetails"
                      :key="i"
                      class="aml-hit-card mb-2"
                      style="cursor:pointer"
                      @click="$set(expandedHistHits, log.id + '_' + i, !expandedHistHits[log.id + '_' + i])"
                    >
                      <div class="d-flex align-center justify-space-between">
                        <div class="d-flex align-center" style="gap:6px;flex:1;min-width:0">
                          <v-icon x-small :color="expandedHistHits[log.id + '_' + i] ? '#185FA5' : '#bbb'">
                            {{ expandedHistHits[log.id + '_' + i] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                          </v-icon>
                          <span style="font-size:12px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ hit.name }}</span>
                          <span class="aml-type-badge">{{ hit.source_code }}</span>
                          <span class="aml-type-badge">{{ hit.data_set }}</span>
                        </div>
                        <span class="aml-pill" style="background:#FCEBEB;color:#A32D2D;flex-shrink:0">{{ hit.score }}</span>
                      </div>
                      <div v-if="expandedHistHits[log.id + '_' + i]" class="mt-2 pt-2" style="border-top:0.5px solid #eee">
                        <div v-if="hit.aliases" class="aml-muted mb-1" style="font-size:11px"><strong>Aliases:</strong> {{ hit.aliases }}</div>
                        <div v-if="hit.address" class="aml-muted mb-1" style="font-size:11px"><strong>Address:</strong> {{ hit.address }}</div>
                        <div v-if="hit.last_updated" class="aml-muted mb-1" style="font-size:11px"><strong>List date:</strong> {{ describeListDate(hit.last_updated, hit.date_method) }}</div>
                        <div v-if="log.searchParameters && log.searchParameters.listEdition && log.searchParameters.listEdition.list_updated_at" class="aml-muted mb-1" style="font-size:11px">
                          <strong>Screened against list of:</strong> {{ describeListDate(log.searchParameters.listEdition.list_updated_at, log.searchParameters.listEdition.date_method) }}
                        </div>
                        <a v-if="isSafeUrl(hit.source_url)" :href="hit.source_url" target="_blank" rel="noopener noreferrer" class="aml-source-link mb-1" @click.stop>
                          <v-icon x-small color="#185FA5">mdi-open-in-new</v-icon>View at source
                        </a>
                        <div class="d-flex" style="gap:4px;flex-wrap:wrap">
                          <span class="aml-type-badge">{{ hit.source_name }}</span>
                          <span class="aml-type-badge">{{ hit.type }}</span>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- ══ Open Case Dialog ══ -->
    <v-dialog v-model="openCaseDialog" max-width="520" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="primary">{{ hasActiveCase ? 'mdi-folder-plus-outline' : 'mdi-folder-open-outline' }}</v-icon>
          <span class="subtitle-1 font-weight-medium">{{ hasActiveCase ? 'Link flag to existing case' : 'Open investigation' }}</span>
          <v-spacer /><v-btn icon @click="openCaseDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <!-- Active case exists -->
          <v-alert
            v-if="hasActiveCase && activeCaseRef"
            dense outlined type="info"
            class="mb-4"
            style="font-size:12px;border-radius:8px"
          >
            Case <strong>{{ activeCaseRef.caseReference }}</strong> is already active for this account.
            This flag will be linked to that case — no new case will be created.
          </v-alert>
          <v-form ref="caseForm">
            <v-select
              v-if="!hasActiveCase"
              v-model="caseForm.priority"
              :items="['HIGH','MEDIUM','LOW']"
              label="Priority" dense outlined
              prepend-inner-icon="mdi-flag-outline"
              class="mb-3"
              :rules="[v => !!v || 'Required']"
            />
            <v-autocomplete
              v-if="!hasActiveCase"
              v-model="caseForm.assignedToUserId"
              :items="users"
              :loading="loadingUsers"
              item-text="displayName"
              item-value="id"
              label="Assign to officer (optional)"
              dense outlined clearable
              no-data-text="No users found"
              class="mb-3"
            />
            <v-textarea
              v-if="!hasActiveCase"
              v-model="caseForm.summary"
              label="Summary (optional)"
              rows="3" dense outlined
            />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="openCaseDialog = false">Cancel</v-btn>
          <v-btn depressed color="primary" :loading="openingCase" @click="submitOpenCase">
            <v-icon left small>{{ hasActiveCase ? 'mdi-folder-plus-outline' : 'mdi-folder-open-outline' }}</v-icon>
            {{ hasActiveCase ? 'Link to existing case' : 'Open case' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ Re-search Override Dialog ══ -->
    <v-dialog v-model="overrideDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="orange darken-2">mdi-refresh</v-icon>
          <span class="subtitle-1 font-weight-medium">Allow re-search</span>
          <v-spacer /><v-btn icon @click="overrideDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="body-2 mb-4">
            This flag has a resolved outcome (<strong>{{ flagStatusLabel(flag.flagStatus) }}</strong>). Setting a re-search override will allow a new search to be run against <strong>{{ providerLabel(flag.provider) }}</strong> for this account.
          </div>
          <v-textarea v-model="overrideReason" label="Reason for re-search *" rows="3" dense outlined :rules="[v => !!v || 'Required']" />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="overrideDialog = false">Cancel</v-btn>
          <v-btn depressed color="orange darken-2" dark :loading="settingOverride" :disabled="!overrideReason.trim()" @click="submitOverride">
            <v-icon left small>mdi-refresh</v-icon>Allow re-search
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState }         from 'vuex';
import PageMixin            from '@/mixins/page.mixin';
import FormatMixin          from '@/mixins/format.mixin';
import AmlMixin             from '@/mixins/aml.mixin';
import { WatchlistService }  from '@/modules/amlpro/services/watchlist.service';
import { UserService }       from '@/modules/global-settings/security/services/user.service';
import {
  providerLabel, providerShort, describeListDate, isSafeUrl,
} from '@/modules/amlpro/services/watchlist-sources';

const svc     = new WatchlistService();
const userSvc = new UserService();

export default {
  name: 'WatchlistFlagDetail',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  props: { flagId: { type: String, required: true } },

  data() {
    return {
      loading:       true,
      flag:          null,
      tab:           0,
      expandedLog:   null,

      // Open case dialog
      openCaseDialog: false,
      openingCase:    false,
      users:          [],
      loadingUsers:   false,
      caseForm:       { priority: 'MEDIUM', assignedToUserId: null, summary: '' },

      overrideDialog:  false,
      settingOverride: false,
      overrideReason:  '',

      latestHitsExpanded:  false,
      latestHitsPerPage:   3,
      expandedLatestHits:  {}, 
      expandedHistHits:    {},
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    tabDefs() {
      return [
        { label: 'Overview' },
        { label: 'Search history', badge: this.flag?.searchLogs?.length ?? 0 },
      ];
    },

    latestLog() {
      return this.flag?.searchLogs?.[0] ?? null;
    },

    canOpenCase() {
      return this.flag?.flagStatus === 'POSSIBLE_MATCH_UNREVIEWED';
    },

    hasActiveCase() {
      if (!this.flag?.cases) return false;
      return this.flag.cases.some(c => ['OPEN', 'REOPENED'].includes(c.status));
    },

    activeCaseRef() {
      if (!this.flag?.cases) return null;
      return this.flag.cases.find(c => ['OPEN', 'REOPENED'].includes(c.status)) ?? null;
    },

    canSetOverride() {
      return ['TRUE_POSITIVE', 'FALSE_POSITIVE'].includes(this.flag?.flagStatus) && !this.flag?.allowResearch;
    },
  },

  mounted() {
    this.loadFlag();
  },

  methods: {
    providerLabel, providerShort, describeListDate, isSafeUrl,

    async loadFlag() {
      this.loading = true;
      try {
        const res  = await svc.fetchFlag(this.flagId);
        this.flag  = res.data ?? res;
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load flag' });
      } finally {
        this.loading = false;
      }
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const res  = await userSvc.findAll();
        const data = res.data ?? res;
        const list = data.users ?? data;
        this.users = list.map(u => ({
          id: u.id,
          displayName: u.person ? `${u.person.firstName ?? ''} ${u.person.lastName ?? ''}`.trim() || u.username : u.username,
        }));
      } catch { /* silent */ } finally { this.loadingUsers = false; }
    },

    async submitOpenCase() {
      if (!this.$refs.caseForm.validate()) return;
      this.openingCase = true;
      try {
        const res    = await svc.openCaseFromFlag(this.flagId, { ...this.caseForm, flagId: this.flagId });
        const result = res.data ?? res;

        if (result.alreadyExists) {
          this.showSnack({
            color:   'info',
            title:   'Case already active',
            message: `Case ${result.caseReference} is already open. Taking you there now.`,
          });
        } else {
          this.showSnack({
            color:   'success',
            title:   'Case opened',
            message: `Case ${result.caseReference} created successfully`,
          });
        }
        this.openCaseDialog = false;
        this.goToCase(result.id);
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to open case';
        this.showSnack({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.openingCase = false;
      }
    },

    async submitOverride() {
      if (!this.overrideReason.trim()) return;
      this.settingOverride = true;
      try {
        await svc.setResearchOverride(this.flagId, this.overrideReason);
        this.showSnack({ color: 'success', title: 'Override set', message: 'Re-search override is now active for this flag' });
        this.overrideDialog = false;
        this.overrideReason = '';
        this.loadFlag();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to set override' });
      } finally {
        this.settingOverride = false;
      }
    },

    goToCase(id) { this.$router.push({ name: 'watchlist-case-detail', params: { caseId: id } }); },

    async openCaseDialogAndLoadUsers() {
      this.openCaseDialog = true;
      if (!this.users.length) await this.loadUsers();
      // Pre-populate with logged-in user
      if (this.loggedInUser && !this.caseForm.assignedToUserId) {
        this.caseForm.assignedToUserId = this.loggedInUser.id;
      }
    },

    flagStatusLabel(s) {
      const map = { POSSIBLE_MATCH_UNREVIEWED: 'Unreviewed', UNDER_REVIEW: 'Under review', TRUE_POSITIVE: 'Confirmed hit', FALSE_POSITIVE: 'Cleared' };
      return map[s] ?? s;
    },

    flagStatusStyle(s) {
      const map = {
        POSSIBLE_MATCH_UNREVIEWED: 'background:#FCEBEB;color:#A32D2D',
        UNDER_REVIEW:              'background:#E6F1FB;color:#185FA5',
        TRUE_POSITIVE:             'background:#FCEBEB;color:#A32D2D',
        FALSE_POSITIVE:            'background:#EAF3DE;color:#3B6D11',
      };
      return map[s] ?? 'background:#F1EFE8;color:#5F5E5A';
    },

    matchStatusStyle(s) {
      const map = { POSSIBLE_MATCH: 'background:#FCEBEB;color:#A32D2D', NO_MATCH: 'background:#EAF3DE;color:#3B6D11' };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

    caseStatusStyle(s) {
      const map = { OPEN: 'background:#E6F1FB;color:#185FA5', REOPENED: 'background:#FFF4E5;color:#854F0B', CLOSED_INVESTIGATION_COMPLETED: 'background:#EAF3DE;color:#3B6D11', CLOSED_NO_ACTION: 'background:#F1EFE8;color:#5F5E5A', CLOSED_REPORT_FILED: 'background:#EEEDFE;color:#534AB7' };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },
  },
};
</script>

<style scoped>
.aml-detail-root  { font-family:inherit; }
.aml-detail-wrap  { padding:20px 24px 32px; }
.aml-detail-header { display:flex; align-items:center; margin-bottom:20px; flex-wrap:wrap; gap:8px; }
.aml-back-btn     { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; }
.aml-back-btn:hover { background:#f5f5f5; }
.aml-case-ref     { font-size:20px; font-weight:500; letter-spacing:-0.3px; }
.aml-case-sub     { font-size:12px; color:#999; margin-top:2px; }
.aml-header-divider { width:1px; height:24px; background:#e0e0e0; margin:0 4px; align-self:center; }
.aml-hdr-btn      { font-size:12px; padding:5px 12px; border-radius:8px; border:0.5px solid #ddd; background:transparent; color:inherit; cursor:pointer; display:inline-flex; align-items:center; gap:4px; transition:background 0.12s; font-family:inherit; }
.aml-hdr-btn:hover { background:#f5f5f5; }
.aml-hdr-btn--primary { background:#185FA5; color:#fff; border-color:#185FA5; }
.aml-hdr-btn--primary:hover { background:#0C447C; }
.aml-status-pill  { display:inline-flex; align-items:center; gap:3px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.aml-tabs-bar     { display:flex; border-bottom:0.5px solid #e0e0e0; margin-bottom:20px; gap:0; overflow-x:auto; }
.aml-tab          { font-size:13px; padding:10px 18px; border:none; background:transparent; color:#888; cursor:pointer; border-bottom:2px solid transparent; transition:all 0.15s; white-space:nowrap; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
.aml-tab--active  { color:#185FA5; border-bottom-color:#185FA5; font-weight:500; }
.aml-tab-badge    { font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; background:#F1EFE8; color:#888; }
.aml-panel        { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; height:100%; }
.aml-panel-head   { display:flex; align-items:center; padding:12px 14px; border-bottom:0.5px solid #e0e0e0; gap:8px; }
.aml-panel-icon   { width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.aml-panel-title  { font-size:13px; font-weight:500; }
.aml-grid-2       { display:grid; grid-template-columns:1fr 1fr; gap:10px 16px; }
.aml-field        {}
.aml-label        { font-size:11px; color:#999; margin-bottom:2px; }
.aml-value        { font-size:12px; }
.aml-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; }
.aml-divider      { height:0.5px; background:#e0e0e0; }
.aml-muted        { color:#777; }
.aml-tag          { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-pill         { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-type-badge   { font-size:10px; font-weight:700; color:#666; background:#f5f5f5; padding:2px 6px; border-radius:4px; }
.aml-ref-link     { color:#185FA5; font-weight:600; }
.aml-empty-panel  { padding:24px; text-align:center; color:#bbb; font-size:12px; }
.aml-table        { width:100%; border-collapse:collapse; font-size:12px; }
.aml-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.aml-table th     { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.aml-table-row    { border-bottom:0.5px solid #e0e0e0; cursor:pointer; transition:background 0.1s; }
.aml-table-row:hover { background:#EBF3FC; }
.aml-table td     { padding:10px 14px; }
.aml-hit-card     { border:0.5px solid #e0e0e0; border-radius:8px; padding:10px 12px; background:#fafafa; }
.aml-show-more-btn { font-size:11px; padding:3px 10px; border-radius:6px; border:0.5px solid #ddd; background:transparent; cursor:pointer; font-family:inherit; color:#185FA5; display:inline-flex; align-items:center; gap:2px; transition:background 0.1s; }
.aml-show-more-btn:hover { background:#EBF3FC; }
.aml-source-link  { color:#185FA5; text-decoration:none; font-size:11px; display:inline-flex; align-items:center; gap:2px; }
.aml-source-link:hover { text-decoration:underline; }
</style>